import { View, StyleSheet, Platform, Linking } from "react-native";
import React, { useCallback, useEffect, useState } from "react";
import { useAppConfig } from "../../../AppConfigProvider";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { useCompletePendingInviteMutation } from "../../../graphql/generated/graphql";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { announceForAccessibility } from "../../../utils/accessibility";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import Logo from "../../../components/general/logos/Logo";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import Loader from "../../../components/common/status/Loader/Loader";
import { PasswordChecklist, validatePassword } from "../../../components/common/password/passwordChecklist";
import { useAuth } from "../../../hooks/useAuth";
import { Checked, UnChecked } from "../../../components/svgs/common";

function SetPasswordContainer() {
  const appConfig = useAppConfig();
  const isCompanionMode = appConfig.isCompanionMode;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { params } = useRoute<RouteProp<RootStackParamList, "Set Password">>();

  const isExistingUser = params?.f === btoa("isExistingUser");

  const service = params?.service;

  const [completePendingInvite, { loading }] = useCompletePendingInviteMutation();

  const [errorMessage, setErrorMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [invitationConfirmed, setInvitationConfirmed] = useState(false);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);

  const handleTermsAgreePress = () => {
    setHasAgreedToTerms((prev) => !prev);
  };

  const { isAuthenticated, logout, userInfo } = useAuth();

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  useEffect(() => {
    if ((!isExistingUser && isAuthenticated) || (params?.uid && params?.uid !== userInfo.userId)) {
      // That means there is an authenticated user but it is not the same user. so we need to log out the current user and log in the new user.
      handleLogout();
    }
  }, [isExistingUser, isAuthenticated, userInfo, params?.uid]);

  useEffect(() => {
    if (Platform.OS === "ios" && errorMessage) {
      announceForAccessibility({ message: "Error alert" + errorMessage });
    }
  }, [errorMessage]);

  const logo = appConfig.logo;

  const handleNewPasswordChange = (text) => {
    setNewPassword(text);
  };

  const handlePasswordToggle = () => {
    setPasswordHidden((prev) => !prev);
  };

  const handleConfirmInvitation = async () => {
    setErrorMessage("");

    if (!isExistingUser) {
      const isValidPassword = validatePassword(newPassword);

      if (isValidPassword !== true) {
        setErrorMessage(isValidPassword as string);
        return;
      }

      if (!hasAgreedToTerms) {
        setErrorMessage("You must agree to the Terms of Service.");
        return;
      }
    }

    if (!params?.token) {
      setErrorMessage("No token found. Please open the link from your email.");
      return;
    }

    try {
      const result = await completePendingInvite({
        variables: {
          token: params?.token,
          newPassword,
          service,
        },
      });

      if (!result.data.completePendingInvite.success) {
        setErrorMessage(result.data.completePendingInvite.message);
        return;
      }

      setInvitationConfirmed(true);

      if (isAuthenticated) {
        navigation.navigate(isCompanionMode ? "Companion Home" : "Home");
      }
    } catch (error) {
      setErrorMessage("Operation failed.");
      return;
    }
  };

  return (
    <View style={[styles.container, { maxWidth: 400, width: "100%" }]}>
      <View accessible={true} accessibilityRole="header" style={styles.container}>
        <Logo uri={logo} height={25} width={92} />
        <CustomSpacing type="vertical" size="xl" />
        <CustomSpacing type="vertical" size="xl" />
        <CustomText
          style={undefined}
          text={isExistingUser ? "Please confirm invitation" : "Create a password"}
          size="s"
          weight="ultraBold"
          textType="display"
        />
        <CustomSpacing type="vertical" size="m" />
        {!isExistingUser && (
          <CustomText style={{ color: "#667085" }} text={"Set a password to secure your account."} size="s" />
        )}
      </View>
      {!invitationConfirmed ? (
        <>
          {isExistingUser ? (
            loading ? (
              <Loader />
            ) : (
              <>
                <CustomSpacing type="vertical" size="xxl" />
                <CustomButton
                  disabled={undefined}
                  leftIcon={undefined}
                  rightIcon={undefined}
                  textStyle={undefined}
                  textProps={undefined}
                  text="Confirm Invitation"
                  styleType="primary"
                  style={styles.button}
                  onPress={handleConfirmInvitation}
                />
                {errorMessage ? (
                  <>
                    <CustomSpacing type="vertical" size="s" />
                    <CustomText role="alert" text={errorMessage} size="s" style={styles.errorMessage} />
                    <CustomSpacing type="vertical" size="s" />
                  </>
                ) : (
                  <CustomSpacing type="vertical" size="xl" />
                )}
              </>
            )
          ) : (
            <View style={styles.container}>
              <View style={styles.passwordContainer}>
                <CustomSpacing type="vertical" size="m" />
                <CustomText style={undefined} text="Password" size="s" />
                <CustomSpacing type="vertical" size="xs" />
                <CustomTextInput
                  style={errorMessage && styles.textInputError}
                  value={newPassword}
                  placeholder="New Password..."
                  onChangeText={(text) => handleNewPasswordChange(text)}
                  secureTextEntry={passwordHidden}
                  onToggleSecureEntry={handlePasswordToggle}
                  aria-label="Password text input"
                  accessibilityHint="Enter your password"
                />
                <CustomSpacing type="vertical" size="xl" />
                <PasswordChecklist rules={["length", "number", "capital", "lowercase"]} value={newPassword} />
              </View>
              <CustomSpacing type="vertical" size="m" />
              <View style={styles.termsContainer}>
                <CustomButton
                  styleType="transparent"
                  role="checkbox"
                  aria-checked={hasAgreedToTerms}
                  aria-label="Agree to terms"
                  style={styles.termsButton}
                  leftIcon={hasAgreedToTerms ? <Checked /> : <UnChecked />}
                  onPress={handleTermsAgreePress}
                  useTranslationText={false}
                />
                <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                  <CustomText useTranslationText={false} style={styles.termsText} text="I agree to the " />
                  <CustomText
                    role="link"
                    style={styles.termsLink}
                    text="Terms of Service and Privacy Policy"
                    onPress={() => Linking.openURL(appConfig.termsOfServiceLink)}
                    useTranslationText={false}
                  />
                </View>
              </View>

              <CustomSpacing type="vertical" size="m" />

              {loading ? (
                <Loader />
              ) : (
                <CustomButton
                  accessibilityHint="Press to set password"
                  text="Set Password and Log In"
                  styleType="primary"
                  onPress={handleConfirmInvitation}
                  disabled={undefined}
                  leftIcon={undefined}
                  rightIcon={undefined}
                  textStyle={undefined}
                  textProps={undefined}
                  style={styles.button}
                />
              )}
              <CustomSpacing type="vertical" size="m" />

              <View aria-live={errorMessage ? "assertive" : "off"}>
                {errorMessage ? (
                  <>
                    <CustomSpacing type="vertical" size="s" />
                    <CustomText role="alert" text={errorMessage} size="s" style={styles.errorMessage} />
                    <CustomSpacing type="vertical" size="s" />
                  </>
                ) : (
                  <CustomSpacing type="vertical" size="xl" />
                )}
              </View>
            </View>
          )}
        </>
      ) : (
        <>
          <CustomText text="Invitation confirmed" size="s" style={undefined} />
          <CustomSpacing type="vertical" size="m" />
          <CustomButton
            text="Log In"
            styleType="primary"
            onPress={() => navigation.navigate("Login")}
            disabled={undefined}
            leftIcon={undefined}
            rightIcon={undefined}
            textStyle={undefined}
            textProps={undefined}
            style={styles.button}
          />
        </>
      )}
    </View>
  );
}

export default SetPasswordContainer;

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  passwordContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "100%",
  },
  button: {
    justifyContent: "center",
    width: "100%",
  },
  errorMessage: {
    color: "red",
    textAlign: "center",
  },
  textInputError: {
    borderColor: "red",
  },
  termsContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    marginTop: 12,
    marginBottom: 12,
  },
  termsButton: {
    padding: 0,
    marginRight: 10,
  },
  termsText: {
    fontSize: 14,
  },
  termsLink: {
    color: "#3E68FE",
    textDecorationLine: "underline",
  },
});
